import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { formatDateTime } from '@/shared/lib/format';
import type { Draft } from '../api/draftsApi';
import { shortAge } from '../model/draftView';
import { tagSx, tint } from './styles';

const STATUS = {
  pending: { label: 'Waiting', token: 'warning-main', color: 'warning.main' },
  approved: { label: 'Sending', token: 'info-main', color: 'info.main' },
  sending: { label: 'Sending', token: 'info-main', color: 'info.main' },
  sent: { label: 'Sent', token: 'success-main', color: 'success.dark' },
  rejected: { label: 'Rejected', token: 'error-main', color: 'error.main' },
} as const;

function StatusBadge({ draft }: { draft: Draft }) {
  const s = STATUS[draft.status];
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 5, bgcolor: tint(s.token, 12), color: s.color, fontSize: '0.75rem', fontWeight: 600 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} aria-hidden />
      {s.label}{draft.status === 'pending' ? ` · ${shortAge(draft.createdAt)}` : ''}
    </Stack>
  );
}

/** What kind of message, its id and status; then who asked, as what, where and when. */
export function DraftHeader({ draft }: { draft: Draft }) {
  return (
    <Stack sx={{ gap: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
        <Box component="span" sx={tagSx}>{draft.source === 'update' ? 'Research update' : 'Answer'}</Box>
        <Box component="span" sx={tagSx}>{draft.id}</Box>
        <Box sx={{ ml: 'auto' }}><StatusBadge draft={draft} /></Box>
      </Stack>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
        <PersonAvatar name={draft.requester.name} size={34} />
        <Box sx={{ minWidth: 0 }}>
          <Typography id="draft-title" variant="h3" component="h2">From {draft.requester.name}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {draft.requester.role} · via {draft.channel === 'teams' ? 'Teams' : 'the portal'} · {formatDateTime(draft.createdAt)}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}
